import React, { useState, useEffect, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Menu,
  ListItemIcon,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Stack,
  Divider,
  Collapse,
  useMediaQuery,
  useTheme,
  Badge,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import {
  
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  MoreVert as MoreVertIcon,
  WhatsApp as WhatsAppIcon,
  CalendarMonth as CalendarMonthIcon,
  Check as CheckIcon,
  Cancel as CancelIcon,
  Event as EventIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Schedule as ScheduleIcon,
  Psychology as PsychologyIcon,
  MedicalServices as MedicalServicesIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelOutlineIcon,
  HourglassEmpty as PendingIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import citaService from '../../services/citaService';
import sesionEtapaService from '../../services/sesionEtapaService';
import especialidadService from '../../services/especialidadService';
import PageHeader from '../../components/common/PageHeader';
import WhatsAppReminderButton from '../../components/citas/WhatsAppReminderButton';
import AvailabilityScheduler from '../../components/citas/AvailabilityScheduler';
import { debounce } from 'lodash';

const customChipStyles = {
  borderRadius: '16px',
  fontWeight: 'medium',
  padding: '2px 8px',
  fontSize: '0.75rem'
};

const getEstadoColor = (estado) => {
  switch (estado) {
    case 'Programada':
      return 'primary';
    case 'Confirmada':
      return 'success';
    case 'Cancelada':
      return 'error';
    case 'Realizada':
      return 'default';
    case 'No asistió':
      return 'warning';
    case 'Reprogramo':
      return 'info';
    default:
      return 'default';
  }
};

const getEstadoDisplay = (estado) => {
  switch (estado) {
    case 'Programada':
      return 'Confirmada'; // Mostrar Programada como Confirmada
    case 'Cancelada':
      return 'Justificada'; // Mostrar Cancelada como Justificada
    default:
      return estado;
  }
};

const getEstadoAtencionColor = (estado) => {
  switch (estado) {
    case 'atendido':
      return 'success';
    case 'no_atendido':
      return 'error';
    case 'pendiente':
      return 'warning';
    default:
      return 'default';
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    let dateToFormat = dateString;
    
    if (dateString.includes('T')) {
      dateToFormat = dateString.split('T')[0];
    }
    
    const [year, month, day] = dateToFormat.split('-');
    const date = new Date(year, month - 1, day, 12, 0, 0);
    
    return format(date, 'dd/MM/yyyy', { locale: es });
  } catch (error) {
    return dateString;
  }
};

const formatTime = (timeString) => {
  if (!timeString) return '';
  try {
    const [hoursStr, minutesStr] = timeString.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);
    
    if (isNaN(hours) || isNaN(minutes)) return timeString;
    
    let displayHours = hours;
    const period = hours >= 12 ? 'PM' : 'AM';
    
    if (hours === 0) {
      displayHours = 12;
    } else if (hours > 12) {
      displayHours = hours - 12;
    }
    
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch (error) {
    return timeString;
  }
};

const esCitaGrupal = (cita) => {
  return cita.grupo_id !== null && cita.grupo_id !== undefined;
};

// 🆕 Función para obtener pacientes de cita grupal
const getPacientesCitaGrupal = (citasAgrupadas, grupoId) => {
  const pacientesGrupo = [];
  Object.values(citasAgrupadas).forEach(pacienteData => {
    pacienteData.citas.forEach(cita => {
      if (cita.grupo_id === grupoId && !pacientesGrupo.find(p => p.id === cita.paciente?.id)) {
        if (cita.paciente) {
          pacientesGrupo.push(cita.paciente);
        }
      }
    });
  });
  return pacientesGrupo;
};

// 🆕 Función para formatear texto de cita grupal
const formatCitaGrupalText = (cita, citasAgrupadas) => {
  if (!esCitaGrupal(cita)) return null;
  
  const pacientesGrupo = getPacientesCitaGrupal(citasAgrupadas, cita.grupo_id);
  const numeroTotalPacientes = pacientesGrupo.length;
  
  if (numeroTotalPacientes <= 1) return null;
  
  return {
    esGrupal: true,
    totalPacientes: numeroTotalPacientes,
    pacientes: pacientesGrupo,
    nombreGrupo: cita.grupo?.nombre || `Grupo ${cita.grupo_id}`,
    primerPaciente: pacientesGrupo[0]
  };
};

const createDebouncedSearch = (callback, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => callback.apply(null, args), delay);
  };
};

const CitasList = () => {
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const [citasAgrupadas, setCitasAgrupadas] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pacientesOrdenados, setPacientesOrdenados] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Estados para filtros
  const [fechaInicio, setFechaInicio] = useState(new Date());
  const [fechaFin, setFechaFin] = useState(addDays(new Date(), 7));
  const [estadoFilter, setEstadoFilter] = useState('');
  const [especialistaFilter, setEspecialistaFilter] = useState('');
  const [especialidadFilter, setEspecialidadFilter] = useState('');
  const [searchPaciente, setSearchPaciente] = useState('');
  
  // Estados para menú y diálogos
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCita, setSelectedCita] = useState(null);
  const [expandedPacientes, setExpandedPacientes] = useState({});
  
  // Estados para modal de reprogramación
  const [reprogramarModalOpen, setReprogramarModalOpen] = useState(false);
  const [citaParaReprogramar, setCitaParaReprogramar] = useState(null);
  const [selectedSlotReprogramar, setSelectedSlotReprogramar] = useState([]);
  const [loadingReprogramar, setLoadingReprogramar] = useState(false);
  
  // Listas para filtros
  const [especialistas, setEspecialistas] = useState([]);
  const [especialidades, setEspecialidades] = useState([]);
  
  // Estados para eliminar citas posteriores
  const [eliminarPosterioresModalOpen, setEliminarPosterioresModalOpen] = useState(false);
  const [citaParaEliminarPosteriores, setCitaParaEliminarPosteriores] = useState(null);
  const [loadingEliminarPosteriores, setLoadingEliminarPosteriores] = useState(false);
  const [citasPosterioresInfo, setCitasPosterioresInfo] = useState(null);

  const limit = 10;
  
  // Función helper para obtener el rol correctamente
  const getUserRole = () => {
    if (!user) return null;
    if (typeof user.rol === 'object' && user.rol !== null) {
      return user.rol.nombre || user.rol.name || user.rol.tipo || null;
    }
    if (typeof user.rol === 'string') {
      return user.rol;
    }
    return null;
  };
  
  // Verificar permisos para crear/editar - SOLO Admin y Admisión
  const canCreate = () => {
    const userRole = getUserRole();
    return ['Administrador', 'Admisión'].includes(userRole);
  };
  
  // Cargar datos iniciales
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [especialistasRes, especialidadesRes] = await Promise.all([
          citaService.getEspecialistas(),
          citaService.getEspecialidades()
        ]);
        
        setEspecialistas(especialistasRes.data || []);
        setEspecialidades(especialidadesRes.data || []);
      } catch (error) {
        console.error('Error fetching initial data:', error);
        showNotification('Error al cargar los datos iniciales', 'error');
      }
    };
    
    fetchInitialData();
  }, [showNotification]);
  
  // Cargar citas y agrupar por paciente
  // Cargar citas y agrupar por paciente
useEffect(() => {
  const fetchCitas = async () => {
    setLoading(true);
    try {
      const params = {
        fecha_inicio: format(fechaInicio, 'yyyy-MM-dd'),
        fecha_fin: format(fechaFin, 'yyyy-MM-dd'),
        estado: estadoFilter || undefined,
        especialista_id: especialistaFilter || undefined,
        especialidad_id: especialidadFilter || undefined,
        limit: searchPaciente.trim() ? 1000 : 50, // Más resultados si hay búsqueda
      };
      
      const response = await citaService.getCitas(params);
      const citas = response.data.citas || [];
 
      // Filtrar por búsqueda de paciente MEJORADO
      let citasFiltradas = citas;
      if (searchPaciente.trim()) {
        const searchTerms = searchPaciente.toLowerCase().trim().split(/\s+/).filter(term => term.length > 0);
         
        citasFiltradas = citas.filter(cita => {
          if (!cita.paciente) return false;
          
          const nombreCompleto = `${cita.paciente.nombres || ''} ${cita.paciente.apellidos || ''}`.toLowerCase();
          const dni = (cita.paciente.dni || '').toString().toLowerCase();
          const numeroHistoria = (cita.paciente.numero_historia || '').toString().toLowerCase();
          const telefono = (cita.paciente.telefono || '').toString().toLowerCase();
          const whatsapp = (cita.paciente.whatsapp || '').toString().toLowerCase();
          
          // Todos los términos deben coincidir en al menos un campo
          return searchTerms.every(term => 
            nombreCompleto.includes(term) || 
            dni.includes(term) ||
            numeroHistoria.includes(term) ||
            telefono.includes(term) ||
            whatsapp.includes(term)
          );
        });
        
       }
      
      // Fecha actual para filtrar citas futuras
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      
      // Agrupar citas por paciente
      const grouped = citasFiltradas.reduce((acc, cita) => {
        // Solo agrupar citas que NO son eventos personales
        if (cita.es_evento_personal) return acc;
        
        const pacienteKey = cita.paciente_id || 'sin-paciente';
        if (!acc[pacienteKey]) {
          acc[pacienteKey] = {
            paciente: cita.paciente,
            citas: [],
            citasFuturas: [],
            etapaActual: null
          };
        }
        acc[pacienteKey].citas.push(cita);
        
        let fechaCitaStr = cita.fecha;
        if (fechaCitaStr.includes('T')) {
          fechaCitaStr = fechaCitaStr.split('T')[0];
        }
        const [year, month, day] = fechaCitaStr.split('-');
        const fechaCita = new Date(year, month - 1, day, 12, 0, 0);
        
        if (fechaCita >= hoy) {
          acc[pacienteKey].citasFuturas.push(cita);
        }
        
        return acc;
      }, {});
      
      // Ordenar citas dentro de cada paciente
      Object.keys(grouped).forEach(key => {
        grouped[key].citas.sort((a, b) => {
          let fechaA = a.fecha;
          let fechaB = b.fecha;
          
          if (fechaA.includes('T')) fechaA = fechaA.split('T')[0];
          if (fechaB.includes('T')) fechaB = fechaB.split('T')[0];
          
          const dateA = new Date(fechaA + 'T' + a.hora_inicio);
          const dateB = new Date(fechaB + 'T' + b.hora_inicio);
          return dateA - dateB;
        });
        
        grouped[key].citasFuturas.sort((a, b) => {
          let fechaA = a.fecha;
          let fechaB = b.fecha;
          
          if (fechaA.includes('T')) fechaA = fechaA.split('T')[0];
          if (fechaB.includes('T')) fechaB = fechaB.split('T')[0];
          
          const dateA = new Date(fechaA + 'T' + a.hora_inicio);
          const dateB = new Date(fechaB + 'T' + b.hora_inicio);
          return dateA - dateB;
        });
      });
      
      // Cargar sesiones para cada cita con etapa
      const citasConEtapa = citasFiltradas.filter(cita => cita.etapa_id);
      if (citasConEtapa.length > 0) {
        const sesionesPromises = citasConEtapa.map(async (cita) => {
          try {
            const response = await sesionEtapaService.getSesionByCitaId(cita.id);
            return { citaId: cita.id, sesion: response.data };
          } catch (error) {
            return { citaId: cita.id, sesion: null };
          }
        });
        
        const sesionesResults = await Promise.all(sesionesPromises);
        
        // Crear mapa de sesiones por cita ID
        const sesionesMap = {};
        sesionesResults.forEach(result => {
          if (result.sesion) {
            sesionesMap[result.citaId] = result.sesion;
          }
        });
        
        // Agregar sesiones a las citas y determinar etapa actual
        Object.keys(grouped).forEach(key => {
          const pacienteData = grouped[key];
          
          // Agregar sesiones a cada cita
          pacienteData.citas = pacienteData.citas.map(cita => ({
            ...cita,
            sesion: sesionesMap[cita.id] || null
          }));
          
          pacienteData.citasFuturas = pacienteData.citasFuturas.map(cita => ({
            ...cita,
            sesion: sesionesMap[cita.id] || null
          }));
          
          // Buscar la última sesión atendida para determinar etapa actual
          const sesionesAtendidas = pacienteData.citas
            .filter(cita => cita.sesion && cita.sesion.estado_atencion === 'atendido')
            .sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
          
          if (sesionesAtendidas.length > 0 && sesionesAtendidas[0].etapa) {
            pacienteData.etapaActual = sesionesAtendidas[0].etapa.nombre;
          } else {
            pacienteData.etapaActual = 'HCE aperturado';
          }
        });
      } else {
        // Si no hay citas con etapa, todas muestran "HCE aperturado"
        Object.keys(grouped).forEach(key => {
          grouped[key].etapaActual = 'HCE aperturado';
        });
      }
      
      const pacientesConProximaCita = Object.entries(grouped)
        .map(([key, data]) => {
          // Buscar la próxima cita válida (Programada o Confirmada)
          const proximaCita = data.citasFuturas.find(c => 
            ['Programada', 'Confirmada'].includes(c.estado)
          );
          
          // Crear objeto con toda la info necesaria para ordenar
          return {
            key,
            proximaCita,
            fechaOrden: proximaCita ? 
              new Date(`${proximaCita.fecha.split('T')[0]}T${proximaCita.hora_inicio}`) : 
              new Date('9999-12-31T23:59:59'),
            nombreCompleto: data.paciente ? 
              `${data.paciente.nombres} ${data.paciente.apellidos}`.toLowerCase() : 
              'zzz'
          };
        })
        .sort((a, b) => {
          // 1. Primero por fecha/hora de próxima cita
          const diffFecha = a.fechaOrden - b.fechaOrden;
          if (diffFecha !== 0) return diffFecha;
          
          // 2. Si misma fecha/hora, por nombre alfabético
          return a.nombreCompleto.localeCompare(b.nombreCompleto, 'es');
        })
        .map(item => item.key); // Solo necesitamos las keys ordenadas
      
      // Guardar tanto el objeto agrupado como el orden
      setCitasAgrupadas(grouped);
      setPacientesOrdenados(pacientesConProximaCita);
      setTotalPages(Math.ceil(pacientesConProximaCita.length / limit));
      
       
    } catch (error) {
      console.error('Error fetching appointments:', error);
      showNotification('Error al cargar las citas', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  fetchCitas();
}, [fechaInicio, fechaFin, estadoFilter, especialistaFilter, especialidadFilter, searchPaciente, refreshKey, showNotification]);
  
  // Aplicar filtros
  const handleApplyFilters = () => {
    setPage(1);
    setRefreshKey(old => old + 1);
  };
  
  // Limpiar filtros
  const handleClearFilters = () => {
    setFechaInicio(new Date());
    setFechaFin(addDays(new Date(), 7));
    setEstadoFilter('');
    setEspecialistaFilter('');
    setEspecialidadFilter('');
    setSearchPaciente('');
    setPage(1);
    setRefreshKey(old => old + 1);
  };
  
  // Manejar expansión de pacientes
  const togglePacienteExpanded = (pacienteId) => {
    setExpandedPacientes(prev => ({
      ...prev,
      [pacienteId]: !prev[pacienteId]
    }));
  };
  
  // Cambiar página
  const handlePageChange = (event, value) => {
    setPage(value);
  };
  
  // Abrir menú de acciones
  const handleMenuOpen = (event, cita) => {
    setAnchorEl(event.currentTarget);
    setSelectedCita(cita);
  };
  
  // Cerrar menú de acciones
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Confirmar cita
  const handleConfirmCita = async (citaId) => {
    try {
      await citaService.updateCita(citaId, { estado: 'Confirmada' });
      showNotification('Cita confirmada correctamente', 'success');
      setRefreshKey(old => old + 1);
      handleMenuClose();
    } catch (error) {
      console.error('Error confirming appointment:', error);
      showNotification('Error al confirmar la cita', 'error');
    }
  };
  
  // Marcar como realizada
  const handleCompleteCita = async (citaId) => {
    try {
      await citaService.updateCita(citaId, { estado: 'Realizada' });
      showNotification('Cita marcada como realizada', 'success');
      setRefreshKey(old => old + 1);
      handleMenuClose();
    } catch (error) {
      console.error('Error completing appointment:', error);
      showNotification('Error al marcar la cita como realizada', 'error');
    }
  };
  
  // Reprogramar cita
  const handleReprogramarCita = async (citaId) => {
    try {
      // Buscar la cita completa primero
      let citaCompleta = null;
      Object.values(citasAgrupadas).forEach(pacienteData => {
        const citaEncontrada = pacienteData.citas.find(c => c.id === citaId);
        if (citaEncontrada) {
          citaCompleta = citaEncontrada;
        }
      });
      
      if (!citaCompleta) {
        showNotification('Error al obtener los datos de la cita', 'error');
        handleMenuClose();
        return;
      }
      
      // Cambiar el estado de la cita original a "Reprogramo"
      try {
        await citaService.updateCita(citaId, { estado: 'Reprogramo' });
      } catch (updateError) {
        console.error('Error al actualizar estado:', updateError);
        // Continuar aunque falle, para permitir la reprogramación
      }
      
      // Abrir el modal directamente
      setCitaParaReprogramar(citaCompleta);
      setSelectedSlotReprogramar([]);
      setReprogramarModalOpen(true);
      showNotification('Seleccione el nuevo horario para la cita', 'info');
      handleMenuClose();
      
    } catch (error) {
      console.error('Error al iniciar reprogramación:', error);
      showNotification('Error al iniciar la reprogramación', 'error');
    }
  };
  
  // Marcar como no asistió
  const handleNoAsistio = async (citaId) => {
    try {
      await citaService.updateCita(citaId, { estado: 'No asistió' });
      showNotification('Cita marcada como No asistió', 'warning');
      setRefreshKey(old => old + 1);
      handleMenuClose();
    } catch (error) {
      console.error('Error marking as no show:', error);
      showNotification('Error al marcar la cita', 'error');
    }
  };
  
  const handleEliminarCita = async (citaId) => {
  if (!window.confirm('¿Está seguro de que desea eliminar permanentemente esta cita? Esta acción no se puede deshacer.')) {
    return;
  }
  
  try {
    await citaService.deleteCita(citaId);
    showNotification('Cita eliminada permanentemente', 'success');
    setRefreshKey(old => old + 1);
    handleMenuClose();
  } catch (error) {
    console.error('Error deleting appointment:', error);
    showNotification('Error al eliminar la cita', 'error');
  }
};

// Manejar eliminación de cita y posteriores
const handleEliminarCitaYPosteriores = async (citaId) => {
  try {
    // 🆕 PASO 1: Consultar conteo exacto al backend
     const conteoInfo = await citaService.contarCitasPosteriores(citaId);
    
     
    if (conteoInfo.total === 0) {
      showNotification('No se encontraron citas posteriores para eliminar', 'warning');
      return;
    }
    
    // 🆕 PASO 2: Buscar cita en citasAgrupadas para info complementaria
    let citaEncontrada = null;
    Object.values(citasAgrupadas).forEach(pData => {
      const cita = pData.citas.find(c => c.id === citaId);
      if (cita) {
        citaEncontrada = cita;
      }
    });
    
    setCitaParaEliminarPosteriores(citaEncontrada || { id: citaId });
    
    // 🆕 PASO 3: Usar información REAL del backend
    setCitasPosterioresInfo({
      totalCitas: conteoInfo.total,
      paciente: conteoInfo.paciente,
      fechaDesde: conteoInfo.fecha_desde,
      ejemplos: conteoInfo.ejemplos || [],
      esInformacionCompleta: true // Flag para saber que es info del backend
    });
    
    setEliminarPosterioresModalOpen(true);
    handleMenuClose();
    
  } catch (error) {
    console.error('❌ Error al consultar citas posteriores:', error);
    
    // 🆕 FALLBACK: Si falla el backend, usar método original
     
    let citaEncontrada = null;
    let pacienteData = null;
    
    Object.values(citasAgrupadas).forEach(pData => {
      const cita = pData.citas.find(c => c.id === citaId);
      if (cita) {
        citaEncontrada = cita;
        pacienteData = pData;
      }
    });
    
    if (!citaEncontrada) {
      showNotification('Cita no encontrada', 'error');
      return;
    }
    
    // Usar método original como fallback
    const fechaSeleccionada = new Date(citaEncontrada.fecha);
    const citasPosteriores = pacienteData.citas.filter(cita => {
      const fechaCita = new Date(cita.fecha);
      return cita.especialista_id === citaEncontrada.especialista_id &&
             fechaCita >= fechaSeleccionada &&
             !['Realizada'].includes(cita.estado);
    });
    
    setCitaParaEliminarPosteriores(citaEncontrada);
    setCitasPosterioresInfo({
      totalCitas: citasPosteriores.length,
      citas: citasPosteriores,
      paciente: citaEncontrada.paciente,
      esInformacionCompleta: false // Flag para mostrar advertencia
    });
    
    setEliminarPosterioresModalOpen(true);
    handleMenuClose();
  }
};

// 2️⃣ FRONTEND: Agregar logs en handleConfirmarEliminacionPosteriores
const handleConfirmarEliminacionPosteriores = async () => {
  if (!citaParaEliminarPosteriores) return;
 
  setLoadingEliminarPosteriores(true);
  try {
    const result = await citaService.eliminarCitaYPosteriores(citaParaEliminarPosteriores.id);
    
     
    showNotification(
      `${result.citas_eliminadas || 0} citas eliminadas exitosamente`, 
      'success'
    );
    
    setEliminarPosterioresModalOpen(false);
    setCitaParaEliminarPosteriores(null);
    setCitasPosterioresInfo(null);
    setRefreshKey(old => old + 1);
    
  } catch (error) {
    console.error('❌ PASO 4: Error eliminando citas posteriores:', error);
    console.error('Error details:', {
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });
    
    const errorMessage = error.response?.data?.message || 
                         error.message || 
                         'Error al eliminar las citas posteriores';
    
    showNotification(errorMessage, 'error');
  } finally {
    setLoadingEliminarPosteriores(false);
  }
};

  // Manejar cambio de slots en reprogramación (limitado a uno)
  const handleSlotsChangeReprogramar = (newSlots) => {
    // Limitar a un solo slot seleccionado
    if (newSlots.length > 0) {
      setSelectedSlotReprogramar([newSlots[newSlots.length - 1]]);
    } else {
      setSelectedSlotReprogramar([]);
    }
  };
  
  // Confirmar reprogramación
  const handleConfirmarReprogramacion = async () => {
    if (selectedSlotReprogramar.length === 0) {
      showNotification('Debe seleccionar un horario', 'warning');
      return;
    }
    
    setLoadingReprogramar(true);
    try {
      // Extraer fecha y hora del slot seleccionado
      const slotKey = selectedSlotReprogramar[0];
      const lastDashIndex = slotKey.lastIndexOf('-');
      const dateStr = slotKey.substring(0, lastDashIndex);
      const hour = parseInt(slotKey.substring(lastDashIndex + 1));
      
      // Calcular hora fin basada en la duración original
      const horaInicioOriginal = citaParaReprogramar.hora_inicio.split(':');
      const horaFinOriginal = citaParaReprogramar.hora_fin.split(':');
      const duracionMinutos = 
        (parseInt(horaFinOriginal[0]) * 60 + parseInt(horaFinOriginal[1])) - 
        (parseInt(horaInicioOriginal[0]) * 60 + parseInt(horaInicioOriginal[1]));
      
      const horaFinNueva = Math.floor((hour * 60 + duracionMinutos) / 60);
      const minutosFinNueva = (hour * 60 + duracionMinutos) % 60;
      
      // Crear nueva cita con los mismos datos pero nueva fecha/hora
      const nuevaCitaData = {
        paciente_id: citaParaReprogramar.paciente_id,
        especialista_id: citaParaReprogramar.especialista_id,
        especialidad_id: citaParaReprogramar.especialidad_id,
        programa_id: citaParaReprogramar.programa_id,
        fecha: dateStr,
        hora_inicio: `${hour.toString().padStart(2, '0')}:00`,
        hora_fin: `${horaFinNueva.toString().padStart(2, '0')}:${minutosFinNueva.toString().padStart(2, '0')}`,
        estado: 'Programada',
        motivo: citaParaReprogramar.motivo,
        observaciones: `Reprogramada desde cita #${citaParaReprogramar.id}. ${citaParaReprogramar.observaciones || ''}`.trim(),
        es_evento_personal: citaParaReprogramar.es_evento_personal,
        // Copiar datos de etapa si existen
        etapa_id: citaParaReprogramar.etapa_id,
        numero_sesion: citaParaReprogramar.numero_sesion
      };
      
      // Primero crear la nueva cita
      const nuevaCitaResponse = await citaService.createCita(nuevaCitaData);
      
      // Si se creó exitosamente, actualizar el estado de la cita original
      if (nuevaCitaResponse.data) {
        try {
          await citaService.updateCita(citaParaReprogramar.id, { estado: 'Reprogramo' });
        } catch (updateError) {
          console.error('Error al actualizar estado de cita original:', updateError);
          // No fallar si no se puede actualizar el estado, la nueva cita ya se creó
        }
      }
      
      showNotification('Cita reprogramada exitosamente', 'success');
      setReprogramarModalOpen(false);
      setCitaParaReprogramar(null);
      setSelectedSlotReprogramar([]);
      setRefreshKey(old => old + 1);
      
    } catch (error) {
      console.error('Error al crear nueva cita:', error);
      showNotification('Error al crear la nueva cita', 'error');
    } finally {
      setLoadingReprogramar(false);
    }
  };
  
  // Componente Card para móvil
  const PacienteCard = ({ pacienteData, pacienteKey }) => {
    const isExpanded = expandedPacientes[pacienteKey];
    const { paciente, citas, citasFuturas, etapaActual } = pacienteData;
    const proximaCita = citasFuturas.find(c => ['Programada', 'Confirmada'].includes(c.estado));
    const citasRealizadas = citas.filter(c => c.estado === 'Realizada').length;
    const citasPendientes = citasFuturas.filter(c => ['Programada', 'Confirmada'].includes(c.estado)).length;
    
    return (
      <Card sx={{ mb: 2, boxShadow: 2, borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                {paciente ? `${paciente.nombres} ${paciente.apellidos}` : 'Sin paciente asignado'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                HC: {paciente?.numero_historia || '-'} | DNI: {paciente?.dni || '-'}
              </Typography>
              
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={etapaActual}
                  size="small"
                  color="secondary"
                  icon={<PsychologyIcon />}
                  sx={customChipStyles}
                />

                {citas.some(cita => esCitaGrupal(cita)) && (
                  <Chip
                    label="Tiene citas grupales"
                    size="small"
                    color="info"
                    icon={<PersonIcon />}
                    variant="outlined"
                    sx={customChipStyles}
                  />
                )}

                <Chip
                  label={`${citasPendientes} pendientes`}
                  size="small"
                  color="primary"
                  icon={<ScheduleIcon />}
                  sx={customChipStyles}
                />
                <Chip
                  label={`${citasRealizadas} realizadas`}
                  size="small"
                  color="success"
                  variant="outlined"
                  sx={customChipStyles}
                />
              </Stack>
              
                {proximaCita && (
                  <Box sx={{ mt: 2, p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
                    <Typography variant="caption" color="primary.contrastText">
                      Próxima cita: {formatDate(proximaCita.fecha)} - {formatTime(proximaCita.hora_inicio)}
                    </Typography>
                  </Box>
                )}
            </Box>
            
            <IconButton
              onClick={() => togglePacienteExpanded(pacienteKey)}
              size="small"
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
          
          <Collapse in={isExpanded}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Detalle de citas:
            </Typography>
            
            {citas.map((cita) => {
              const citaGrupalInfo = formatCitaGrupalText(cita, citasAgrupadas);
              
              return (
                <Box key={cita.id} sx={{ 
                  mb: 2, 
                  p: 1, 
                  bgcolor: citaGrupalInfo ? 'info.light' : 'grey.50',
                  borderRadius: 1,
                  border: citaGrupalInfo ? '1px solid' : 'none',
                  borderColor: citaGrupalInfo ? 'info.main' : 'transparent'
                }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="body2">
                        {formatDate(cita.fecha)} - {formatTime(cita.hora_inicio)}
                        {/* 🆕 Indicador de cita grupal */}
                        {citaGrupalInfo && (
                          <Chip
                            label={`GRUPAL (${citaGrupalInfo.totalPacientes})`}
                            size="small"
                            color="info"
                            sx={{ ml: 1, fontSize: '0.7rem' }}
                          />
                        )}
                      </Typography>
                      
                      {/* 🆕 Mostrar pacientes de cita grupal */}
                      {citaGrupalInfo && (
                        <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 0.5 }}>
                          Pacientes: {citaGrupalInfo.pacientes.map(p => `${p.nombres} ${p.apellidos}`).join(', ')}
                        </Typography>
                      )}
                      
                      <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                        <Chip
                          label={getEstadoDisplay(cita.estado)}
                          size="small"
                          color={getEstadoColor(cita.estado)}
                          sx={customChipStyles}
                        />
                        {cita.sesion && (
                          <Chip
                            label={
                              cita.sesion.estado_atencion === 'atendido' ? 'Atendido' :
                              cita.sesion.estado_atencion === 'no_atendido' ? 'No Atendido' :
                              'Pendiente'
                            }
                            size="small"
                            color={getEstadoAtencionColor(cita.sesion.estado_atencion)}
                            variant="outlined"
                            sx={customChipStyles}
                          />
                        )}
                        {cita.etapa && (
                          <Chip
                            label={cita.etapa.nombre}
                            size="small"
                            icon={<PsychologyIcon />}
                            variant="outlined"
                            sx={customChipStyles}
                          />
                        )}
                      </Stack>
                    </Box>
                    
                    <Stack direction="row" spacing={0.5}>
                      <WhatsAppReminderButton
                        citaId={cita.id}
                        cita={cita}
                        disabled={
                          ['Cancelada', 'Realizada', 'No asistió', 'Reprogramo'].includes(cita.estado) ||
                          ['Especialista', 'Interno'].includes(getUserRole())
                        }
                      />
                      <IconButton
                        component={RouterLink}
                        to={`/citas/${cita.id}`}
                        size="small"
                        color="info"
                        disabled={['Especialista', 'Interno'].includes(getUserRole())}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Collapse>
        </CardContent>
      </Card>
    );
  };
  
  // Obtener pacientes paginados
  const getPacientesPaginados = () => {
  // Usar el array ordenado en lugar de Object.keys
  const start = (page - 1) * limit;
  const end = start + limit;
  const keysOrdenadas = pacientesOrdenados.slice(start, end);
  
  // Construir objeto con las keys en el orden correcto
  const resultado = {};
  keysOrdenadas.forEach(key => {
    if (citasAgrupadas[key]) {
      resultado[key] = citasAgrupadas[key];
    }
  });
  
  return resultado;
};
  
  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader 
        title="Gestión de Citas"
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Citas' }
        ]}
        actionButton={
          <Box>
            <Button
              component={RouterLink}
              to="/citas/calendario"
              variant="outlined"
              color="primary"
              startIcon={<CalendarMonthIcon />}
              sx={{ mr: 1 }}
              size={isMobile ? "small" : "medium"}
            >
              {isMobile ? "Calendario" : "Ver Calendario"}
            </Button>
            {canCreate() && (
              <Button
                component={RouterLink}
                to="/citas/nueva"
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                size={isMobile ? "small" : "medium"}
              >
                {isMobile ? "Nueva" : "Nueva Cita"}
              </Button>
            )}
          </Box>
        }
      />
      
      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Fecha Desde"
                value={fechaInicio}
                onChange={(date) => setFechaInicio(date)}
                slots={{
                  textField: TextField,
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: isMobile ? "small" : "medium"
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Fecha Hasta"
                value={fechaFin}
                onChange={(date) => setFechaFin(date)}
                slots={{
                  textField: TextField,
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: isMobile ? "small" : "medium"
                  }
                }}
              />
            </Grid>
            
            {/* Filtro por Paciente - Búsqueda simple */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size={isMobile ? "small" : "medium"}
                label="Buscar Paciente"
                placeholder="Nombre, apellido, DNI, teléfono..."
                value={searchPaciente}
                onChange={(e) => setSearchPaciente(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {loading && searchPaciente.trim() ? <CircularProgress size={20} /> : <SearchIcon />}
                    </InputAdornment>
                  ),
                }}
                helperText={
                  searchPaciente.trim() 
                    ? `Buscando: "${searchPaciente}" - ${Object.keys(citasAgrupadas).length} pacientes encontrados`
                    : "Busque por nombre, apellido, DNI o teléfono"
                }
              />
            </Grid>
            
            {/* Filtro por Especialidad */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>Especialidad</InputLabel>
                <Select
                  value={especialidadFilter}
                  label="Especialidad"
                  onChange={(e) => setEspecialidadFilter(e.target.value)}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {especialidades.map((esp) => (
                    <MenuItem key={esp.id} value={esp.id}>
                      {esp.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={estadoFilter}
                  label="Estado"
                  onChange={(e) => setEstadoFilter(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="Justificada">Justificada</MenuItem>
                  <MenuItem value="Realizada">Realizada</MenuItem>
                  <MenuItem value="No asistió">No asistió</MenuItem>
                  <MenuItem value="Reprogramo">Reprogramó</MenuItem>
                  <MenuItem value="Eliminar">Eliminar</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                <InputLabel>Especialista</InputLabel>
                <Select
                  value={especialistaFilter}
                  label="Especialista"
                  onChange={(e) => setEspecialistaFilter(e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {especialistas.map((especialista) => (
                    <MenuItem key={especialista.id} value={especialista.id}>
                      {especialista.usuario ? 
                        `${especialista.usuario.nombre} ${especialista.usuario.apellidos}` : 
                        `Especialista #${especialista.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={12} md={3}>
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleApplyFilters}
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                >
                  Filtrar
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleClearFilters}
                  size={isMobile ? "small" : "medium"}
                >
                  Limpiar
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* Contenido principal */}
      <Card>
        <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
          {loading ? (
            <Box display="flex" justifyContent="center" my={5}>
              <CircularProgress />
            </Box>
          ) : Object.keys(citasAgrupadas).length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body1" color="textSecondary">
                No se encontraron citas con los filtros aplicados.
              </Typography>
              {canCreate() && (
                <Button
                  component={RouterLink}
                  to="/citas/nueva"
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  sx={{ mt: 2 }}
                >
                  Agendar Nueva Cita
                </Button>
              )}
            </Box>
          ) : (
            <>
              {/* Vista móvil - Cards */}
              {isMobile ? (
  <Box>
    {/* 🔄 CAMBIAR Object.entries por esto: */}
    {pacientesOrdenados
      .slice((page - 1) * limit, page * limit)
      .map((pacienteKey) => {
        const pacienteData = citasAgrupadas[pacienteKey];
        if (!pacienteData) return null;
        
        return (
          <PacienteCard 
            key={pacienteKey} 
            pacienteData={pacienteData} 
            pacienteKey={pacienteKey}
          />
        );
      })}
  </Box>
) : (
                /* Vista desktop - Tabla */
                <TableContainer component={Paper} elevation={0}>
                  <Table size={isTablet ? "small" : "medium"}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Paciente</TableCell>
                        <TableCell>N° Historia</TableCell>
                        <TableCell align="center">Tipo</TableCell>
                        <TableCell align="center">Citas</TableCell>
                        <TableCell>Etapa Actual</TableCell>
                        <TableCell>Fechas</TableCell>
                        <TableCell>Horas</TableCell>
                        <TableCell>
                          Estados
                          <Typography variant="caption" display="block">
                            (Cita/Sesión)
                          </Typography>
                        </TableCell>
                        <TableCell align="right">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pacientesOrdenados
    .slice((page - 1) * limit, page * limit)
    .map((pacienteKey) => {
      const pacienteData = citasAgrupadas[pacienteKey];
      if (!pacienteData) return null;
      
      const { paciente, citas, citasFuturas, etapaActual } = pacienteData;
      const isExpanded = expandedPacientes[pacienteKey];
      
      return (
        <React.Fragment key={pacienteKey}>
                            <TableRow 
                              sx={{ 
                                '&:hover': { backgroundColor: 'action.hover' },
                                backgroundColor: isExpanded ? 'action.selected' : 'inherit'
                              }}
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                                    {paciente?.nombres?.charAt(0) || 'P'}
                                  </Avatar>
                                  <Box>
                                    <Typography variant="body2" fontWeight="medium">
                                      {paciente ? `${paciente.nombres} ${paciente.apellidos}` : 'Sin paciente'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {paciente?.telefono || paciente?.whatsapp || 'Sin contacto'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>{paciente?.numero_historia || '-'}</TableCell>
                              
                               <TableCell align="center">
                                {/* 🆕 Mostrar tipo de citas */}
                                <Stack direction="column" spacing={0.5}>
                                  {citasFuturas.some(cita => esCitaGrupal(cita)) && (
                                    <Chip
                                      label="Grupales"
                                      size="small"
                                      color="info"
                                      icon={<PersonIcon />}
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  )}
                                  {citasFuturas.some(cita => !esCitaGrupal(cita)) && (
                                    <Chip
                                      label="Individuales"
                                      size="small"
                                      color="default"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Stack>
                              </TableCell>

                              <TableCell align="center">
                                <Badge badgeContent={citasFuturas.length} color="primary">
                                  <CalendarMonthIcon />
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={etapaActual}
                                  size="small"
                                  color="secondary"
                                  icon={<PsychologyIcon />}
                                  sx={customChipStyles}
                                />
                              </TableCell>
                              <TableCell>
                                {citasFuturas.slice(0, 2).map((cita, index) => (
                                  <Typography key={cita.id} variant="body2">
                                    {formatDate(cita.fecha)}
                                  </Typography>
                                ))}
                                {citasFuturas.length > 2 && (
                                  <Typography variant="caption" color="text.secondary">
                                    +{citasFuturas.length - 2} más
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {citasFuturas.slice(0, 2).map((cita, index) => (
                                  <Typography key={cita.id} variant="body2">
                                    {formatTime(cita.hora_inicio)}
                                  </Typography>
                                ))}
                                {citasFuturas.length > 2 && (
                                  <Typography variant="caption" color="text.secondary">
                                    ...
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                {citasFuturas.slice(0, 2).map((cita, index) => (
                                  <Box key={cita.id} sx={{ mb: 1 }}>
                                    <Chip
                                      label={getEstadoDisplay(cita.estado)}
                                      size="small"
                                      color={getEstadoColor(cita.estado)}
                                      sx={customChipStyles}
                                    />
                                    {cita.sesion && (
                                      <Chip
                                        label={
                                          cita.sesion.estado_atencion === 'atendido' ? 'Atendido' :
                                          cita.sesion.estado_atencion === 'no_atendido' ? 'No Atendido' :
                                          'Pendiente'
                                        }
                                        size="small"
                                        color={getEstadoAtencionColor(cita.sesion.estado_atencion)}
                                        variant="outlined"
                                        sx={{ ...customChipStyles, ml: 0.5 }}
                                      />
                                    )}
                                  </Box>
                                ))}
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  size="small"
                                  onClick={() => togglePacienteExpanded(pacienteKey)}
                                >
                                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                              </TableCell>
                            </TableRow>
                            
                            {/* Fila expandida con detalle de citas */}
                            <TableRow>
                              <TableCell colSpan={8} sx={{ p: 0 }}>
                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                  <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Detalle de todas las citas del paciente (historial completo):
                                    </Typography>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Fecha</TableCell>
                                          <TableCell>Horario</TableCell>
                                          <TableCell>Especialista</TableCell>
                                          <TableCell>Especialidad</TableCell>
                                          <TableCell>Etapa</TableCell>
                                          <TableCell>Estado Cita</TableCell>
                                          <TableCell>Estado Sesión</TableCell>
                                          <TableCell align="right">Acciones</TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {citas.map((cita) => (
                                          <TableRow key={cita.id}>
                                            <TableCell>{formatDate(cita.fecha)}</TableCell>
                                            <TableCell>
                                              {formatTime(cita.hora_inicio)} - {formatTime(cita.hora_fin)}
                                            </TableCell>
                                            <TableCell>
                                              {cita.especialista?.usuario ? 
                                                `${cita.especialista.usuario.nombre} ${cita.especialista.usuario.apellidos}` : 
                                                '-'}
                                            </TableCell>
                                            <TableCell>
                                              {cita.especialidad?.nombre || '-'}
                                            </TableCell>
                                            <TableCell>
                                              {cita.etapa ? (
                                                <Chip
                                                  label={`${cita.etapa.nombre}${cita.numero_sesion ? ` #${cita.numero_sesion}` : ''}`}
                                                  size="small"
                                                  icon={<PsychologyIcon />}
                                                  variant="outlined"
                                                  color="secondary"
                                                  sx={customChipStyles}
                                                />
                                              ) : '-'}
                                            </TableCell>
                                            <TableCell>
                                              <Chip
                                                label={getEstadoDisplay(cita.estado)}
                                                size="small"
                                                color={getEstadoColor(cita.estado)}
                                                sx={customChipStyles}
                                              />
                                            </TableCell>
                                            <TableCell>
                                              {cita.sesion ? (
                                                <Chip
                                                  icon={
                                                    cita.sesion.estado_atencion === 'atendido' ? <CheckCircleIcon /> :
                                                    cita.sesion.estado_atencion === 'no_atendido' ? <CancelOutlineIcon /> :
                                                    <PendingIcon />
                                                  }
                                                  label={
                                                    cita.sesion.estado_atencion === 'atendido' ? 'Atendido' :
                                                    cita.sesion.estado_atencion === 'no_atendido' ? 'No Atendido' :
                                                    'Pendiente'
                                                  }
                                                  size="small"
                                                  color={getEstadoAtencionColor(cita.sesion.estado_atencion)}
                                                  variant="outlined"
                                                  sx={customChipStyles}
                                                />
                                              ) : '-'}
                                            </TableCell>
                                            <TableCell align="right">
                                              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                <WhatsAppReminderButton
                                                  citaId={cita.id}
                                                  cita={cita}
                                                  disabled={
                                                    ['Cancelada', 'Realizada', 'No asistió', 'Reprogramo'].includes(cita.estado) ||
                                                    ['Especialista', 'Interno'].includes(getUserRole())
                                                  }
                                                />
                                                <Tooltip title="Ver detalles">
                                                  <span>
                                                    <IconButton
                                                      component={RouterLink}
                                                      to={`/citas/${cita.id}`}
                                                      size="small"
                                                      color="info"
                                                      disabled={['Especialista', 'Interno'].includes(getUserRole())}
                                                    >
                                                      <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                  </span>
                                                </Tooltip>
                                                <Tooltip title="Más acciones">
                                                  <IconButton
                                                    size="small"
                                                    onClick={(e) => handleMenuOpen(e, cita)}
                                                  >
                                                    <MoreVertIcon fontSize="small" />
                                                  </IconButton>
                                                </Tooltip>
                                              </Box>
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
          
          {/* Paginación */}
          {!loading && Object.keys(citasAgrupadas).length > 0 && (
            <Box p={2} display="flex" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size={isMobile ? "small" : "medium"}
                showFirstButton={!isMobile}
                showLastButton={!isMobile}
              />
            </Box>
          )}
        </CardContent>
      </Card>
      
      {/* Menú de acciones - Solo 4 opciones ahora */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedCita && canCreate() && (
          <>
            {selectedCita.estado === 'Programada' && (
              <MenuItem onClick={() => handleConfirmCita(selectedCita.id)}>
                <ListItemIcon>
                  <CheckIcon fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText primary="Confirmar Cita" />
              </MenuItem>
            )}
            
            {selectedCita.estado === 'Confirmada' && (
              <MenuItem onClick={() => handleCompleteCita(selectedCita.id)}>
                <ListItemIcon>
                  <EventIcon fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText primary="Marcar como Realizada" />
              </MenuItem>
            )}
            
            {(selectedCita.estado === 'Programada' || 
              selectedCita.estado === 'Confirmada' || 
              selectedCita.estado === 'Cancelada') && (
              <>
                <MenuItem onClick={() => handleReprogramarCita(selectedCita.id)}>
                  <ListItemIcon>
                    <ScheduleIcon fontSize="small" color="info" />
                  </ListItemIcon>
                  <ListItemText primary="Reprogramar" />
                </MenuItem>
                
                <MenuItem onClick={() => handleNoAsistio(selectedCita.id)}>
                  <ListItemIcon>
                    <CancelOutlineIcon fontSize="small" color="warning" />
                  </ListItemIcon>
                  <ListItemText primary="No Asistió" />
                </MenuItem>

                <MenuItem 
                  onClick={() => handleEliminarCita(selectedCita.id)}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemIcon>
                    <CancelIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText primary="Eliminar Permanentemente" />
                </MenuItem>

                <MenuItem 
                  onClick={() => handleEliminarCitaYPosteriores(selectedCita.id)}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText primary="Eliminar Esta y Posteriores" />
                </MenuItem>

              </>
            )}
          </>
        )}
        
        <MenuItem 
          component={RouterLink} 
          to={selectedCita ? `/pacientes/${selectedCita.paciente_id}` : '#'}
          onClick={handleMenuClose}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Ver Paciente" />
        </MenuItem>
      </Menu>
      
      {/* Modal de Reprogramación */}
      <Dialog
        open={reprogramarModalOpen}
        onClose={() => {
          setReprogramarModalOpen(false);
          setCitaParaReprogramar(null);
          setSelectedSlotReprogramar([]);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          Reprogramar Cita
          {citaParaReprogramar && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Paciente: {citaParaReprogramar.paciente?.nombres} {citaParaReprogramar.paciente?.apellidos} | 
              Especialidad: {citaParaReprogramar.especialidad?.nombre}
            </Typography>
          )}
        </DialogTitle>
        
        <DialogContent>
          {citaParaReprogramar && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Seleccione el nuevo horario para la cita. Solo puede seleccionar un horario disponible.
              </Alert>
              
              <Box sx={{ height: 'calc(100% - 80px)', overflow: 'auto' }}>
                <AvailabilityScheduler
                  selectedEspecialista={citaParaReprogramar.especialista}
                  selectedSlots={selectedSlotReprogramar}
                  onSlotsChange={handleSlotsChangeReprogramar}
                  disabled={false}
                />
              </Box>
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => {
              setReprogramarModalOpen(false);
              setCitaParaReprogramar(null);
              setSelectedSlotReprogramar([]);
            }}
            disabled={loadingReprogramar}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarReprogramacion}
            variant="contained"
            disabled={selectedSlotReprogramar.length === 0 || loadingReprogramar}
            startIcon={loadingReprogramar ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {loadingReprogramar ? 'Reprogramando...' : 'Confirmar Reprogramación'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Eliminación de Citas Posteriores */}
      <Dialog
        open={eliminarPosterioresModalOpen}
        onClose={() => {
          setEliminarPosterioresModalOpen(false);
          setCitaParaEliminarPosteriores(null);
          setCitasPosterioresInfo(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Eliminar Cita y Todas las Posteriores
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>¡Atención!</strong> Esta acción eliminará permanentemente la cita seleccionada 
            y todas las citas posteriores del mismo paciente con el mismo especialista.
          </Alert>
          
          {citaParaEliminarPosteriores && citasPosterioresInfo && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Detalles de la eliminación:
              </Typography>
              
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Paciente:</strong> {citasPosterioresInfo.paciente || 
                    (citaParaEliminarPosteriores.paciente ? 
                      `${citaParaEliminarPosteriores.paciente.nombres} ${citaParaEliminarPosteriores.paciente.apellidos}` : 
                      'Sin información')}
                </Typography>
                
                <Typography variant="body2">
                  <strong>Desde la fecha:</strong> {formatDate(
                    citasPosterioresInfo.fechaDesde || citaParaEliminarPosteriores.fecha
                  )}
                </Typography>
                
                <Typography variant="body2" color="error" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                  <strong>Total de citas a eliminar: {citasPosterioresInfo.totalCitas}</strong>
                </Typography>
                
                {/* 🆕 Mostrar advertencia si es info limitada */}
                {!citasPosterioresInfo.esInformacionCompleta && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="caption">
                      ⚠️ Este conteo se basa solo en las citas visibles. 
                      El número real podría ser mayor e incluir citas fuera del rango de fechas actual.
                    </Typography>
                  </Alert>
                )}
                
                {/* 🆕 Mostrar ejemplos si los hay */}
                {citasPosterioresInfo.ejemplos && citasPosterioresInfo.ejemplos.length > 0 && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    <Typography variant="body2" gutterBottom>
                      <strong>Ejemplos de citas que serán eliminadas:</strong>
                    </Typography>
                    {citasPosterioresInfo.ejemplos.map((cita, index) => (
                      <Typography key={index} variant="body2" sx={{ ml: 2 }}>
                        • {formatDate(cita.fecha)} - {cita.hora} ({cita.estado})
                      </Typography>
                    ))}
                    {citasPosterioresInfo.totalCitas > citasPosterioresInfo.ejemplos.length && (
                      <Typography variant="body2" sx={{ ml: 2, fontStyle: 'italic', color: 'primary.main' }}>
                        ... y {citasPosterioresInfo.totalCitas - citasPosterioresInfo.ejemplos.length} citas más
                      </Typography>
                    )}
                  </Alert>
                )}
              </Stack>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => {
              setEliminarPosterioresModalOpen(false);
              setCitaParaEliminarPosteriores(null);
              setCitasPosterioresInfo(null);
            }}
            disabled={loadingEliminarPosteriores}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmarEliminacionPosteriores}
            variant="contained"
            color="error"
            disabled={loadingEliminarPosteriores || !citasPosterioresInfo?.totalCitas}
            startIcon={loadingEliminarPosteriores ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {loadingEliminarPosteriores ? 'Eliminando...' : `Eliminar ${citasPosterioresInfo?.totalCitas || 0} Citas`}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default CitasList;